import { useState, useEffect, useMemo } from 'react';
import { useMDSContext } from '@metservice/use-mds';
import { GeoJSONSource, Source } from 'mapbox-gl';
import { generateSource, renderLayer, sourceId } from 'gridded-datasets-config';
import { wktToGeoJson } from 'utils/wktToGeoJson';
import { booleanPointInPolygon } from '@turf/turf';
import { generalDataset } from '@mono/types/src/mds';
import { toHash } from '@mono/to-hash';
import { useSpaceConfiguratorContext } from 'space-configurator-context';

export const useGriddedDatasetSelector = () => {
    const { state, enableGriddedDataset, map } = useSpaceConfiguratorContext();
    // Datasets states
    const { findDatasets } = useMDSContext();
    const [allDatasets, setAllDatasets] = useState<generalDataset[]>();

    const [layersHaveBeenSetup, setLayersHaveBeenSetup] = useState(false);

    // Load the dataset, given the bounds of the map
    useEffect(() => {
        if (!map) return;
        if (!enableGriddedDataset) return;

        const abortController = new AbortController();
        const getDatasets = async () => {
            try {
                const mapBounds = map.getBounds();
                const bbox: [number, number, number, number] = [mapBounds.getWest(), mapBounds.getSouth(), mapBounds.getEast(), mapBounds.getNorth()];
                const response = await findDatasets(
                    {
                        bbox,
                        data_type: ['grid'],
                        type: ['model'],
                        temporal_nature: ['forecast'],
                    },
                    abortController.signal
                );

                setAllDatasets(response['datasource-datasets']);
            } catch (error) {
                console.warn(error);
                if (error.toString().includes('The user aborted a request')) return;
                throw error;
            }
        };

        map.on('moveend', getDatasets);

        return () => {
            map.off('moveend', getDatasets);
        };
    }, [map, enableGriddedDataset]);

    // Effect to filter gridded datasets by selectedDatasetId or space
    const griddedDatasets = useMemo(() => {
        if (!state.space) return;

        const dataSetsForPoint = allDatasets?.filter((dataset) => {
            if (!state.space) return false;

            const polygon = wktToGeoJson(dataset.spatial_boundary) as GeoJSON.Polygon;
            if (!polygon) {
                console.log(`Current dataset bounds are not a polygon`);
                return false;
            }
            const isPointInPolygon = booleanPointInPolygon(state.space.coordinates, polygon);

            return isPointInPolygon;
        });

        return dataSetsForPoint;
    }, [toHash(state.space), allDatasets?.length]);

    // Effect to setup map layers
    useEffect(() => {
        if (!map) return;

        const existingSource = map.getSource(sourceId);

        if (!existingSource) {
            map.addSource(sourceId, {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [],
                },
                promoteId: 'id',
            });
        }

        const existingRenderLayer = map.getLayer(renderLayer.id);
        if (!existingRenderLayer) {
            map.addLayer(renderLayer as any);
        }

        setLayersHaveBeenSetup(true);
    }, [map]);

    // Effect to edit map source data.
    useEffect(() => {
        if (!map) return;
        if (!griddedDatasets) return;
        if (!layersHaveBeenSetup) return;

        const isGeoJsonSource = (source?: Source): source is GeoJSONSource => source?.type === 'geojson';
        const currentlySelectedDataset = griddedDatasets.find((dataset) => dataset.id === state.griddedDatasetId);
        const existingSource = map.getSource(sourceId);

        if (!currentlySelectedDataset) {
            if (isGeoJsonSource(existingSource)) {
                existingSource.setData({
                    type: 'FeatureCollection',
                    features: [],
                });
            }
            return;
        }

        const sourceData = generateSource(currentlySelectedDataset);

        if (isGeoJsonSource(existingSource)) {
            existingSource.setData(sourceData);
        }
    }, [layersHaveBeenSetup, griddedDatasets?.length, state.griddedDatasetId]);

    return { griddedDatasets };
};
